import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  ComponentRef,
  OnDestroy,
  EmbeddedViewRef,
  ElementRef
} from '@angular/core';
import { Observable, Subscription, isObservable } from 'rxjs';
import { IsLoadingSpinnerComponent } from './is-loading-spinner.component';

/**
 * Structural directive that shows a loading spinner or custom template while data is loading.
 *
 * Accepted input types:
 * - `boolean` — `true` shows spinner, `false` shows content
 * - `Observable<boolean>` — subscribes and reacts to emitted values
 * - any object — `null` / `undefined` shows spinner, any non-null value shows content
 *
 * When the content is shown, the resolved value is exposed as a template variable via
 * the `as` syntax or `let` binding, eliminating the need for a separate `*ngIf`.
 *
 * @example
 * <!-- Boolean -->
 * <div *isLoading="isDataLoading">
 *   {{ data }}
 * </div>
 *
 * @example
 * <!-- Observable<boolean> -->
 * <div *isLoading="loading$">
 *   {{ data }}
 * </div>
 *
 * @example
 * <!-- Arbitrary object: null = spinner, non-null = content -->
 * <div *isLoading="myObject">
 *   {{ myObject.name }}
 * </div>
 *
 * @example
 * <!-- 'as' binding: resolves Observable and binds value in one step -->
 * <div *isLoading="data$ | async as data">
 *   {{ data.name }}
 * </div>
 *
 * @example
 * <!-- With loading message -->
 * <div *isLoading="loading$; message: 'Loading data...'">
 *   {{ data }}
 * </div>
 *
 * @example
 * <!-- With custom spinner template -->
 * <div *isLoading="loading$; spinner: customSpinner">
 *   {{ data }}
 * </div>
 * <ng-template #customSpinner>
 *   <p>Loading...</p>
 * </ng-template>
 */
@Directive({
  selector: '[isLoading]'
})
export class IsLoadingDirective implements OnDestroy {
  private loadingSub?: Subscription;
  private currentLoading: boolean | null = null;
  private spinnerRef?: ComponentRef<IsLoadingSpinnerComponent> | EmbeddedViewRef<any>;
  private resolvedValue: any = undefined;

  /** Enables `*isLoading="obs$ | async as foo"` — foo receives the resolved value. */
  static ngTemplateContextGuard<T>(dir: IsLoadingDirective, ctx: any): ctx is { $implicit: T } {
    return true;
  }

  /**
   * Controls the loading state. Accepts:
   * - `boolean` / `Observable<boolean>`: true = loading
   * - any object: `null` / `undefined` = loading, non-null = content shown
   */
  @Input()
  set isLoading(value: boolean | Observable<boolean> | any) {
    this.handleLoadingChange(value);
  }

  /**
   * Optional custom spinner template.
   * Use as: *isLoading="loading$; spinner: myTemplate"
   */
  @Input('isLoadingSpinner') spinnerTemplate?: TemplateRef<any>;

  /**
   * Optional message to display below the spinner.
   * Use as: *isLoading="loading$; message: 'Please wait...'"
   */
  @Input('isLoadingMessage') 
  set message(value: string | undefined) {
    this._message = value;
    // If spinner is already showing, update its message
    if (this.currentLoading && this.spinnerRef && 'instance' in this.spinnerRef) {
      this.spinnerRef.instance.message = value;
    }
  }
  get message(): string | undefined {
    return this._message;
  }
  private _message?: string;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  private handleLoadingChange(value: boolean | Observable<boolean> | any): void {
    // Unsubscribe from previous observable if any
    this.loadingSub?.unsubscribe();
    this.loadingSub = undefined;

    if (isObservable(value)) {
      this.loadingSub = value.subscribe((v: any) => {
        this.resolvedValue = v;
        this.updateView(this.resolveLoading(v));
      });
    } else {
      this.resolvedValue = value;
      this.updateView(this.resolveLoading(value));
    }
  }

  private resolveLoading(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'boolean') return value;
    return false;
  }

  private updateView(loading: boolean): void {
    // Skip update if state hasn't changed
    if (loading === this.currentLoading) {
      return;
    }

    this.currentLoading = loading;
    this.updateAriaBusy(loading);
    
    // Clear current view
    this.viewContainer.clear();
    this.spinnerRef = undefined;

    if (loading) {
      this.showLoadingSpinner();
    } else {
      this.showContent();
    }
  }

  private showLoadingSpinner(): void {
    if (this.spinnerTemplate) {
      // Use custom spinner template if provided
      this.spinnerRef = this.viewContainer.createEmbeddedView(this.spinnerTemplate);
    } else {
      // Create default spinner component (Angular Ivy syntax)
      this.spinnerRef = this.viewContainer.createComponent(IsLoadingSpinnerComponent);
      
      // Pass message to spinner component if provided
      if (this._message) {
        this.spinnerRef.instance.message = this._message;
      }
    }
  }

  private showContent(): void {
    // Show the original content, exposing the resolved value so templates can use:
    //   *isLoading="obs$ | async as foo"  → let-foo="isLoading"  (Angular microsyntax 'as')
    //   *isLoading="obs$ | async; let foo" → let-foo             ($implicit)
    this.viewContainer.createEmbeddedView(this.templateRef, {
      $implicit: this.resolvedValue,
      isLoading: this.resolvedValue
    });
  }

  private updateAriaBusy(loading: boolean): void {
    // Get the parent element of the structural directive's anchor comment
    const hostElement = this.elementRef.nativeElement.parentElement;
    
    if (!hostElement) {
      return;
    }

    if (loading) {
      hostElement.setAttribute('aria-busy', 'true');
    } else {
      hostElement.removeAttribute('aria-busy');
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    this.loadingSub?.unsubscribe();
    
    // Ensure aria-busy is removed if directive is destroyed while loading
    this.updateAriaBusy(false);
    
    // Clear any remaining views
    this.viewContainer.clear();
    this.spinnerRef = undefined;
  }
}